import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, SimpleChanges, OnChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { of, Subject } from 'rxjs';
import { take, tap, debounceTime, distinctUntilChanged, takeUntil, finalize } from 'rxjs/operators';
import { State } from 'src/app/store';
import { SetUserSearchActiveForm, SetUserSearchPageOptionsAction, SetUserSearchQuickForm } from 'src/app/user/store/user.actions';
import { userSearchPageOptionsSelector } from 'src/app/user/store/user.selectors';
import { AvailableUserSearchForms, UserResponse } from 'src/app/user/user.model';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { AccessControlService } from 'src/app/global/services/access-control/access-control.abstract.service';
import { TranslateService } from '@ngx-translate/core';

interface User {
    id: string;
    name: string;
    email: string;
    avatar: string;
    selected?: boolean;
    isAssigned?: boolean; // New property to track if user is already assigned to role
    isDisabled?: boolean; // New property to track if user should be disabled
    birthDate?: string;
    phone?: string;
}

// Interface for caching search results
interface SearchCache {
    [searchTerm: string]: {
        results: User[];
        timestamp: number;
    };
}

@Component({
    selector: 'app-search-dropdown',
    templateUrl: './search-dropdown.component.html',
    styleUrls: ['./search-dropdown.component.scss'],
})
export class SearchDropdownComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

    searchForm: FormGroup;
    searchControl = new FormControl('');

    isLoading = false;
    isDropdownOpen = false;
    availableUsers: User[] = [];
    assignedUsers: User[] = [];

    filteredUsers: User[] = [];

    // Store assigned user IDs for matching against search results
    private assignedUserIds: string[] = [];
    // Store assigned user emails as fallback for matching
    private assignedUserEmails: string[] = [];

    // Search caching
    private searchCache: SearchCache = {};
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
    private lastSearchTerm = '';

    private preventClose = false;
    private closeTimeout: any;

    // State management for selected users
    private _selectedUsers: User[] = [];

    @Input()
    get selectedUsers(): User[] {
        return this._selectedUsers;
    }
    set selectedUsers(users: User[]) {
        this._selectedUsers = [...users];
        this.syncUserStates();
    }

    @Input() mode: 'single' | 'multi' = 'multi'; // Default to single selection
    @Input() assignedUsersToRole: User[] = [];
    @Input() roleId: string | null = null; // New input for role ID
    @Input() showCreateOption = false;
    @Input() showRadio = true;

    @Output() usersSelected = new EventEmitter<User[]>();
    @Output() noUsersFound = new EventEmitter<boolean>();
    @Output() createOptionClicked = new EventEmitter<string>();

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private store: Store<State>,
        private userService: UserService,
        private accessControlService: AccessControlService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {
        this.searchForm = this.fb.group({
            search: this.searchControl,
        });
    }

    ngOnInit(): void {
        // Setup debounced search
        this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((searchTerm: string) => {
            if (searchTerm && searchTerm.trim().length > 0) {
                this.performSearch(searchTerm.trim());
            } else {
                this.availableUsers = [];
                this.emitNoUsersFound(false);
            }
        });

        this.emitNoUsersFound(false);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedUsers && changes.selectedUsers.currentValue) {
            this._selectedUsers = [...changes.selectedUsers.currentValue];
            this.syncUserStates();
        }

        if (changes.assignedUsersToRole && changes.assignedUsersToRole.currentValue) {
            // Store assigned user IDs for matching
            this.assignedUserIds = changes.assignedUsersToRole.currentValue
                .map((user: any) => {
                    // User is already typed as 'any' so we can access any properties
                    const possibleId = user._id || user.id || user.userId || user.user_id;
                    return possibleId;
                })
                .filter((id) => id !== undefined);

            // Also extract emails as fallback
            this.assignedUserEmails = changes.assignedUsersToRole.currentValue.map((user: any) => user.email).filter((email) => email);

            // Re-mark current search results if any exist
            if (this.availableUsers.length > 0) {
                this.availableUsers = this.markAssignedUsers(this.availableUsers);
            }

            this.syncUserStates();
        }

        // Load assigned users when role ID changes
        if (changes.roleId && changes.roleId.currentValue && changes.roleId.currentValue !== changes.roleId.previousValue) {
            this.loadAssignedUsersForRole(changes.roleId.currentValue);
        }
    }

    private loadAssignedUsersForRole(roleId: string): void {
        if (!roleId) return;

        this.accessControlService
            .getRoleUsers(roleId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response && response.data) {
                        // Try different possible ID fields
                        this.assignedUserIds = response.data
                            .map((user) => {
                                // Check for various possible ID fields based on the API response structure
                                // The UsersRole interface shows 'id' but the actual response might differ
                                // Cast to any to access potentially undefined properties
                                const userAny = user as any;
                                const possibleId = userAny._id || user.id || userAny.userId || userAny.user_id;
                                return possibleId;
                            })
                            .filter((id) => id !== undefined); // Remove undefined values

                        // Also extract emails as fallback for matching
                        this.assignedUserEmails = response.data.map((user) => user.email).filter((email) => email);

                        // Re-sync current search results if any exist
                        if (this.availableUsers.length > 0) {
                            this.syncUserStates();
                        }
                    }
                },
                error: (error) => {
                    console.error('Error loading assigned users for role:', error);
                    this.assignedUserIds = [];
                },
            });
    }

    private syncUserStates(): void {
        const selectedUserIds = this._selectedUsers.map((user) => user.id);

        // Update available users selected state and check if they're assigned
        this.availableUsers.forEach((user) => {
            user.selected = selectedUserIds.includes(user.id);

            // Check both ID and email for assignment status
            const isAssignedById = this.assignedUserIds.includes(user.id);
            const isAssignedByEmail = this.assignedUserEmails.includes(user.email);
            user.isAssigned = isAssignedById || isAssignedByEmail;
            user.isDisabled = user.isAssigned; // Disable if assigned
        });
    }

    onInputFocus(): void {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }

        this.isDropdownOpen = true;
        const searchTerm = this.searchControl.value || '';

        // If we have a search term, trigger search immediately
        if (searchTerm.trim().length > 0) {
            this.performSearch(searchTerm.trim());
        } else {
            // Clear available users when no search term
            this.availableUsers = [];
        }

        this.emitNoUsersFound(this.availableUsers.length === 0 && searchTerm.length > 0);
    }

    onInputBlur(): void {
        if (!this.preventClose) {
            this.closeTimeout = setTimeout(() => {
                if (!this.preventClose) {
                    this.isDropdownOpen = false;
                }
            }, 200);
        }
    }

    toggleDropdown(): void {
        this.isDropdownOpen = !this.isDropdownOpen;
        if (this.isDropdownOpen) {
            this.searchInput.nativeElement.focus();
        }
    }

    toggleUserSelection(user: User): void {
        // Don't allow selection of disabled/assigned users
        if (user.isDisabled || user.isAssigned) {
            return;
        }

        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }

        if (this.mode === 'single') {
            // In single mode, deselect all others and select only this one
            this.availableUsers.forEach((u) => {
                u.selected = false;
            });
            this._selectedUsers = [{ ...user }]; // Create a new object to avoid reference issues

            // Close dropdown after selection in single mode
            this.isDropdownOpen = false;
        } else {
            const isCurrentlySelected = this.isUserSelected(user);

            if (isCurrentlySelected) {
                // Remove user from selection
                this._selectedUsers = this._selectedUsers.filter((u) => u.id !== user.id);
            } else {
                // Add user to selection
                this._selectedUsers = [...this._selectedUsers, { ...user }];
            }

            // Keep dropdown open in multi mode
            this.isDropdownOpen = true;
            this.preventClose = true;
            setTimeout(() => {
                this.preventClose = false;
            }, 100);
        }

        // Update selected state for both assigned and available users
        this.syncUserStates();

        // Emit updated selection
        this.emitSelectedUsers();

        const searchTerm = this.searchControl.value || '';
        this.emitNoUsersFound(this.availableUsers.length === 0 && searchTerm.length > 0);
    }

    isUserSelected(user: User): boolean {
        return this._selectedUsers.some((selectedUser) => selectedUser.id === user.id);
    }

    private emitSelectedUsers(): void {
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.usersSelected.emit([...this._selectedUsers]);
        }, 0);
    }

    private emitNoUsersFound(value: boolean): void {
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.noUsersFound.emit(value);
        }, 0);
    }

    trackByUserId(index: number, user: User): string {
        return user.id;
    }

    onDropdownMouseEnter(): void {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
        this.preventClose = true;
    }

    onDropdownMouseLeave(): void {
        this.preventClose = false;
        const searchInput = this.searchInput?.nativeElement;
        if (searchInput && document.activeElement !== searchInput) {
            this.closeTimeout = setTimeout(() => {
                if (!this.preventClose) {
                    this.isDropdownOpen = false;
                }
            }, 200);
        }
    }

    onDropdownClick(event: Event): void {
        event.stopPropagation();
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
        this.preventClose = true;
        setTimeout(() => {
            this.preventClose = false;
        }, 100);
    }

    onCreateOptionClick(): void {
        this.createOptionClicked.emit(this.searchControl.value || '');
        this.isDropdownOpen = false;
    }

    private mapUserResponseToUser = (userResponse: UserResponse): User => ({
        id: userResponse._id,
        name: `${userResponse.firstName} ${userResponse.lastName}`.trim(),
        email: userResponse.email,
        avatar: userResponse.picture || 'https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png',
        isAssigned: false,
        isDisabled: false,
        birthDate: userResponse.birthDate,
        phone: userResponse.phone,
    });

    private mapUsersRoleToUser = (usersRole: any): User => ({
        id: usersRole.id,
        name: `${usersRole.firstName} ${usersRole.lastName}`.trim(),
        email: usersRole.email,
        avatar: usersRole.picture || 'https://www.pphfoundation.ca/wp-content/uploads/2018/05/default-avatar.png',
        isAssigned: false,
        isDisabled: false,
        birthDate: usersRole.birthDate,
        phone: usersRole.phone,
    });

    private isSearchCacheValid(searchTerm: string): boolean {
        const cached = this.searchCache[searchTerm];
        if (!cached) return false;

        const now = Date.now();
        return now - cached.timestamp < this.CACHE_DURATION;
    }

    private getCachedSearchResults(searchTerm: string): User[] | null {
        if (this.isSearchCacheValid(searchTerm)) {
            return [...this.searchCache[searchTerm].results]; // Return a copy
        }
        return null;
    }

    private cacheSearchResults(searchTerm: string, results: User[]): void {
        this.searchCache[searchTerm] = {
            results: [...results], // Store a copy
            timestamp: Date.now(),
        };
    }

    /**
     * Mark users who are already assigned to the role as disabled
     * @param users Array of users to process
     * @returns Array with assigned users marked as disabled but still visible
     */
    private markAssignedUsers(users: User[]): User[] {
        return users.map((user) => {
            // Check both ID and email for matching (email as fallback)
            const isAssignedById = this.assignedUserIds.includes(user.id);
            const isAssignedByEmail = this.assignedUserEmails.includes(user.email);
            const isAssigned = isAssignedById || isAssignedByEmail;

            return {
                ...user,
                isAssigned,
                isDisabled: isAssigned,
            };
        });
    }

    private clearExpiredCache(): void {
        const now = Date.now();
        Object.keys(this.searchCache).forEach((key) => {
            if (now - this.searchCache[key].timestamp >= this.CACHE_DURATION) {
                delete this.searchCache[key];
            }
        });
    }

    private performSearch(searchTerm: string): void {
        if (!searchTerm || searchTerm.trim().length === 0) {
            this.availableUsers = [];
            this.isLoading = false;
            this.emitNoUsersFound(false);
            return;
        }

        // Check if this is the same search term as last time
        if (searchTerm === this.lastSearchTerm) {
            return; // Don't search again for the same term
        }

        // Check cache first
        const cachedResults = this.getCachedSearchResults(searchTerm);
        if (cachedResults) {
            // Mark assigned users as disabled in cached results
            this.availableUsers = this.markAssignedUsers(cachedResults);
            this.syncUserStates();
            this.isDropdownOpen = true;
            this.emitNoUsersFound(this.availableUsers.length === 0);
            return;
        }

        this.lastSearchTerm = searchTerm;
        this.isLoading = true;

        this.userService
            .quickSearch(searchTerm.trim(), 1, 10, undefined)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoading = false;
                    this.cdr.detectChanges();
                }),
            )
            .subscribe({
                next: (result) => {
                    if (result && result.docs) {
                        this.isLoading = false;
                        const searchResults = result.docs.map(this.mapUserResponseToUser);

                        // Cache the original results (before marking)
                        this.cacheSearchResults(searchTerm, searchResults);

                        // Mark assigned users as disabled in search results
                        this.availableUsers = this.markAssignedUsers(searchResults);

                        this.syncUserStates();

                        const hasResults = this.availableUsers.length > 0;
                        this.emitNoUsersFound(!hasResults);

                        // Show dropdown if we have results
                        if (hasResults) {
                            this.isDropdownOpen = true;
                        }
                    } else {
                        this.availableUsers = [];
                        this.emitNoUsersFound(true);
                    }

                    // Clean up expired cache entries
                    this.clearExpiredCache();
                },
                error: (error) => {
                    console.error('Error searching users:', error);
                    this.availableUsers = [];
                    this.emitNoUsersFound(true);
                },
            });

        // Dispatch to store
        this.search(searchTerm);
    }

    private search(searchValue?: string): void {
        this.store
            .select(userSearchPageOptionsSelector)
            .pipe(
                take(1),
                tap((options) => {
                    this.store.dispatch(
                        SetUserSearchPageOptionsAction({
                            pageIndex: 0,
                            itemsPerPage: options.itemsPerPage,
                        }),
                    );
                }),
            )
            .subscribe();

        const searchTerm = searchValue || this.searchControl.value;

        of({ search: searchTerm })
            .pipe(
                take(1),
                tap((values) =>
                    this.store.dispatch(
                        SetUserSearchQuickForm({
                            quickSearchForm: { search: values.search },
                        }),
                    ),
                ),
            )
            .subscribe();

        this.store.dispatch(
            SetUserSearchActiveForm({
                activeForm: AvailableUserSearchForms.quickSearch,
            }),
        );
    }
}
